import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Vendor, User } from '@completion/models';
import { AddUsersDialogComponent } from '../add-users-dialog/add-users-dialog.component';
import { Store } from '@ngrx/store';
import { LoadVendors, UpdateVendor } from '@completion/actions';
import { getCurrentProject, getVendors } from '@completion/selectors';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatSort, Sort } from '@angular/material/sort';

@Component({
  selector: 'app-manage-suppliers',
  templateUrl: './manage-suppliers.component.html',
  styleUrls: ['./manage-suppliers.component.scss'],
  standalone: false,
})
export class ManageSuppliersComponent implements OnInit, OnDestroy, AfterViewInit {
  displayedColumns: string[] = ['rowNumber', 'vendorName', 'users', 'actions'];
  dataSource = new MatTableDataSource<Vendor>();
  @ViewChild(MatSort) sort: MatSort;

  destoy$ = new Subject<void>();
  constructor(
    private store: Store,
    private dialog: MatDialog,
  ) {}

  ngOnDestroy(): void {
    this.destoy$.next();
    this.destoy$.complete();
  }

  ngOnInit(): void {
    // Dispatch action to load vendors

    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destoy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new LoadVendors());
        }
      });

    // Select vendors from the store
    this.store.select(getVendors).subscribe((vendors: Vendor[]) => {
      if (vendors && vendors.length > 0) {
        this.dataSource.data = vendors;
      }
    });
    // Customize filtering to search vendor name + user names
    this.dataSource.filterPredicate = (vendor: Vendor, filter: string) => {
      if (!vendor) return false;
      const target = [vendor.name, ...(vendor.users || []).map((u) => u.name)]
        .filter(Boolean)
        .join(' ') // combine
        .toLowerCase();
      return target.includes(filter);
    };
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  applyFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = (input?.value || '').trim().toLowerCase();
    this.dataSource.filter = value;
  }

  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = [...this.dataSource.data];
      return;
    }
    const asc = sort.direction === 'asc';
    this.dataSource.data = [...this.dataSource.data].sort((a, b) => {
      switch (sort.active) {
        case 'vendorName': {
          const av = a.name ?? '';
          const bv = b.name ?? '';
          return asc ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        default:
          return 0;
      }
    });
  }

  removeUser(vendor: Vendor, user: User): void {
    // Update vendor's user list
    const updatedVendor = {
      ...vendor,
      users: vendor.users.filter((u) => u.id !== user.id),
    };

    // Dispatch action to update vendor
    this.store.dispatch(new UpdateVendor(updatedVendor));
  }

  openAddUsersDialog(vendor: Vendor): void {
    const dialogRef = this.dialog.open(AddUsersDialogComponent, {
      width: '1100px', // widened from 900px
      maxWidth: '95vw', // responsive fallback so it doesn't exceed viewport on smaller screens
      data: { vendor },
    });

    dialogRef.afterClosed().subscribe((selectedUsers: User[]) => {
      if (selectedUsers) {
        // Ensure vendor.users is initialized as an array
        const updatedVendor = {
          ...vendor,
          users: Array.isArray(vendor.users) ? [...vendor.users, ...selectedUsers] : [...selectedUsers],
        };

        // Dispatch action to update vendor
        this.store.dispatch(new UpdateVendor(updatedVendor));
      }
    });
  }
}
